import { Link } from "react-router-dom";
import { MessageCircle, BookOpen, Feather, Heart } from "lucide-react";

export function StatCard({ label, value, sub, testid }) {
  return (
    <div className="gold-frame paper-scroll rounded-sm p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass mb-2">{label}</div>
      <div className="font-mono-brass text-2xl sm:text-3xl text-[#F7F3EC] mb-1">{value}</div>
      <div className="text-xs text-[#A8A095] font-serif-body italic">{sub}</div>
    </div>
  );
}

export function ReactionsInbox({ reactions }) {
  return (
    <section className="mb-14">
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mb-2 flex items-center gap-2">
        <MessageCircle className="w-5 h-5 text-[#D9B472]" /> Anonymous notes
      </h2>
      <p className="text-sm text-[#A8A095] font-serif-body italic mb-6">One line each reader carried out of your manuscripts and characters. Only you see these.</p>
      {reactions.length === 0 ? (
        <p className="text-[#6E675E] font-serif-body italic">No notes yet. Once buyers unlock your fragments or characters they can send you a line.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="reactions-inbox">
          {reactions.map(({ reaction, work_title, kind }) => (
            <blockquote key={reaction.id} className="gold-frame paper-scroll rounded-sm p-5" data-testid={`reaction-${reaction.id}`}>
              <p className="font-serif-body italic text-lg text-[#F7F3EC] leading-relaxed mb-3">"{reaction.line}"</p>
              <div className="flex items-center justify-between pt-3 border-t border-[rgba(217,180,114,0.1)]">
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">
                  Anonymous reader · <span className="text-[#D9B472]">{kind === "character" ? "character" : "fragment"}</span> · {work_title}
                </div>
                <div className="text-[10px] text-[#6E675E] font-mono-brass">{new Date(reaction.created_at).toLocaleDateString()}</div>
              </div>
            </blockquote>
          ))}
        </div>
      )}
    </section>
  );
}

export function WorksList({ myWorks }) {
  return (
    <section className="mb-14">
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mb-6">Your fragments</h2>
      {myWorks.length === 0 ? (
        <p className="text-[#6E675E] font-serif-body italic">You haven't listed any unfinished work yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {myWorks.map((w) => (
            <div key={w.id} className="gold-frame paper-scroll rounded-sm p-5" data-testid={`my-work-${w.id}`}>
              <div className="flex items-start justify-between mb-2">
                <span className="chip">{w.genre}</span>
                <span className="font-mono-brass text-xs text-[#D9B472]">${(w.price_cents / 100).toFixed(2)}</span>
              </div>
              <Link to={`/work/${w.id}`} className="font-serif-display text-xl text-[#F7F3EC] hover:text-[#E6B860] transition-colors duration-200 block mb-3">
                {w.title}
              </Link>
              <div className="grid grid-cols-3 gap-2 text-xs font-mono-brass">
                <div><span className="text-[#6E675E] block text-[10px] uppercase tracking-widest">Sales</span><span className="text-[#F7F3EC]">${((w.total_sales_cents || 0) / 100).toFixed(2)}</span></div>
                <div><span className="text-[#6E675E] block text-[10px] uppercase tracking-widest">Tips</span><span className="text-[#F7F3EC]">${((w.total_tips_cents || 0) / 100).toFixed(2)}</span></div>
                <div><span className="text-[#6E675E] block text-[10px] uppercase tracking-widest">Draft</span><span className="text-[#F7F3EC]">{w.completion_percent}%</span></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function PurchasesList({ purchases }) {
  return (
    <section className="mb-14">
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mb-6">Unlocked by you</h2>
      {purchases.length === 0 ? (
        <p className="text-[#6E675E] font-serif-body italic">You haven't unlocked any manuscripts yet. <Link to="/browse" className="text-[#D9B472] not-italic">Browse the archive →</Link></p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {purchases.map(({ purchase, work }) => (
            <Link key={purchase.work_id} to={`/work/${work.id}`} className="gold-frame paper-scroll rounded-sm p-5 flex items-center justify-between" data-testid={`purchase-${work.id}`}>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass mb-1">Unlocked · {new Date(purchase.purchased_at).toLocaleDateString()}</div>
                <div className="font-serif-display text-xl text-[#F7F3EC]">{work.title}</div>
                <div className="text-sm text-[#A8A095] italic">by {work.author_pseudonym}</div>
              </div>
              <BookOpen className="w-5 h-5 text-[#D9B472]" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export function FollowingList({ follows, followerCount }) {
  return (
    <section className="mb-14">
      <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F7F3EC] mb-2 flex items-center gap-2">
        <Heart className="w-5 h-5 text-[#D9B472]" /> Pen names you follow
      </h2>
      <p className="text-sm text-[#A8A095] font-serif-body italic mb-6">
        We email you the moment they publish something new. {followerCount != null && (
          <span className="text-[#D9B472] not-italic">· {followerCount} {followerCount === 1 ? "reader follows" : "readers follow"} you</span>
        )}
      </p>
      {!follows || follows.length === 0 ? (
        <p className="text-[#6E675E] font-serif-body italic">
          You aren't following anyone yet. Open a{" "}
          <Link to="/browse" className="text-[#D9B472] not-italic">pen name's storefront</Link>
          {" "}and tap Follow.
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="following-list">
          {follows.map((f) => (
            <Link
              key={f.id}
              to={`/author/${encodeURIComponent(f.author_pseudonym)}`}
              className="gold-frame paper-scroll rounded-sm p-4 flex items-center gap-3 hover:border-[#D9B472] transition-colors duration-200"
              data-testid={`following-${f.author_pseudonym}`}
            >
              <Feather className="w-4 h-4 text-[#D9B472] shrink-0" />
              <div className="min-w-0">
                <div className="font-serif-display text-lg text-[#F7F3EC] italic truncate">{f.author_pseudonym}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">Followed {new Date(f.created_at).toLocaleDateString()}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
